import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, CreditCard, ArrowLeft } from 'lucide-react';
import { proposalsApi } from '../utils/api';

/**
 * /add-payment-method/:pathId
 *
 * Thin redirect-only page: on mount, requests a Stripe Checkout setup session
 * for the given client_path and forwards the browser to the returned URL.
 *
 * Used as an entry point for "Add a payment method" links outside of the
 * contract-signing flow. The contract-signing page calls the same API
 * directly without routing through this page (see ContractSigning.jsx).
 */
export default function AddPaymentMethod() {
  const { pathId } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [redirecting, setRedirecting] = useState(true);
  // Guard against React 18 StrictMode double-invocation in dev — we don't
  // want to create two Stripe sessions on a single page mount.
  const startedRef = useRef(false);

  const start = async () => {
    setError('');
    setRedirecting(true);
    try {
      const res = await proposalsApi.setupPaymentMethod(pathId);
      if (!res?.url) throw new Error('Stripe setup URL was missing from the response.');
      window.location.href = res.url;
    } catch (e) {
      setError(e.message || 'Failed to start payment setup.');
      setRedirecting(false);
    }
  };

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathId]);

  if (error) {
    return (
      <div className="proposal-page">
        <div className="proposal-topbar">
          <button onClick={() => navigate(`/contract/${pathId}`)} className="back-nav-button">
            <ArrowLeft size={16} /> Back
          </button>
        </div>
        <section className="contract-page-card">
          <div className="contract-page-card-icon">
            <AlertCircle size={24} />
          </div>
          <div className="contract-page-card-body">
            <h2>Couldn't start payment setup</h2>
            <p>{error}</p>
            <button
              type="button"
              className="proposal-action-btn proposal-accept-btn"
              onClick={() => { startedRef.current = false; start(); }}
            >
              <CreditCard size={16} /> Try again
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="proposal-page">
      <div className="contract-page-redirect">
        <Loader2 className="spinner-large" size={40} />
        <h2>Redirecting to secure payment setup…</h2>
        <p>
          You'll be sent to Stripe to enter your card or bank details. Please don't close
          this tab.
        </p>
      </div>
    </div>
  );
}
